import React, { useCallback, useEffect, useMemo } from "react"

import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil"
import type { SetterOrUpdater } from "recoil"

import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import type { GetSiteQuery, UpdateSiteInput } from "~/graphql/client"
import { useUpdateSiteMutation } from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { useExitPrompt } from "~/hooks/useExitPrompt"
import { diff } from "~/utils/diff"
import type { Accessors } from "~/utils/mapToInputs"
import { createInputBuilder, get } from "~/utils/mapToInputs"
import { getFrom } from "~/utils/recoil"

import type { SaveOptions } from "./site-state"
import {
  resetLocationZoomState,
  shouldRetainZoomState,
  siteDiffState,
  siteLocalState,
  siteRemoteState,
} from "./site-state"

type Site = GetSiteQuery[`site`]

export type SiteActions = {
  setLocal: SetterOrUpdater<Site>
  changeLocal: (update: Partial<Site>) => void
  save: (options?: SaveOptions) => Promise<void>
  reset: VoidFunction
}

export type SiteStatus = {
  isLoading: boolean
  isSuccess: boolean
}

const SiteActionsContext = React.createContext<SiteActions | null>(null)
const SiteStatusContext = React.createContext<SiteStatus | null>(null)

const useSiteMachinery = (): { actions: SiteActions; status: SiteStatus } => {
  const onError = useDispatchError()
  const { client } = useGraphqlClient()

  const remote = useRecoilValue(siteRemoteState)
  const setRemote = useSetRecoilState(siteRemoteState)
  const setLocal = useSetRecoilState(siteLocalState)

  // Local mirrors remote whenever remote changes (refetch or save). Removing
  // this clobber is a behavior change reserved for a dedicated follow-up.
  useEffect(() => {
    setLocal(remote)
  }, [remote, setLocal])

  const siteUpdater = useUpdateSiteMutation(client, {
    onMutate: (data) => console.debug(`mutating site:`, data),
    onSuccess: (data) =>
      data.updateSite.__typename === `Site`
        ? setRemote(data.updateSite)
        : undefined,
    onError,
  })

  const imageReplacer = useUpdateSiteMutation(client, {
    onMutate: () => console.debug(`replacing images`),
    onSuccess: (data) => {
      if (data.updateSite.__typename === `Site`) {
        setRemote(data.updateSite)
      }
    },
    onError,
  })

  const { mutateAsync: updateSite } = siteUpdater
  const { mutateAsync: replaceImages } = imageReplacer

  // Reads everything from the snapshot at call time so its identity never
  // depends on render state (mutateAsync identities are stable in RQ v4).
  const save = useRecoilCallback(
    ({ snapshot }) => {
      const getSnapshot = getFrom(snapshot)

      return async (options?: SaveOptions) => {
        const remoteSite = getSnapshot(siteRemoteState)
        const localSite = getSnapshot(siteLocalState)
        const zoom = getSnapshot(resetLocationZoomState)
        const shouldRetainZoom = getSnapshot(shouldRetainZoomState)

        const delta = diff(remoteSite, localSite)
        if (delta === null && !options?.repopulateImagery) {
          console.warn(`No changes to save`)

          return
        }

        const updateSiteInput = createUpdateSiteInput(delta ?? {}, remoteSite.id)

        if (shouldRetainZoom) {
          updateSiteInput.zoom = zoom
        }

        if (options?.repopulateImagery) {
          await replaceImages({ updateSiteInput })
        } else {
          if (delta) await updateSite({ updateSiteInput })
        }
      }
    },
    [updateSite, replaceImages],
  )

  const reset = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        set(siteLocalState, getFrom(snapshot)(siteRemoteState))
      },
    [],
  )

  const changeLocal = useCallback(
    (update: Partial<Site>) => {
      setLocal((current) => ({ ...current, ...update }))
    },
    [setLocal],
  )

  const actions = useMemo(
    () => ({ setLocal, changeLocal, save, reset }),
    [setLocal, changeLocal, save, reset],
  )

  const isLoading = siteUpdater.isLoading || imageReplacer.isLoading
  const isSuccess = siteUpdater.isSuccess || imageReplacer.isSuccess

  const status = useMemo(
    () => ({ isLoading, isSuccess }),
    [isLoading, isSuccess],
  )

  return { actions, status }
}

export const SiteContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const { actions, status } = useSiteMachinery()
  const delta = useRecoilValue(siteDiffState)

  useExitPrompt(delta !== null || status.isLoading)

  return (
    <SiteActionsContext.Provider value={actions}>
      <SiteStatusContext.Provider value={status}>
        {children}
      </SiteStatusContext.Provider>
    </SiteActionsContext.Provider>
  )
}

export const useSiteActions = (): SiteActions => {
  const actions = React.useContext(SiteActionsContext)

  if (!actions) {
    throw new Error(`Site context not found`)
  }

  return actions
}

export const useSiteStatus = (): SiteStatus => {
  const status = React.useContext(SiteStatusContext)

  if (!status) {
    throw new Error(`Site context not found`)
  }

  return status
}

export const useSiteContext = (): ReturnType<UseSite> => {
  const { setLocal, changeLocal, save, reset } = useSiteActions()
  const { isLoading, isSuccess } = useSiteStatus()
  const local = useRecoilValue(siteLocalState)
  const delta = useRecoilValue(siteDiffState)

  return useMemo(
    () => ({
      local,
      setLocal,
      changeLocal,
      save,
      reset,
      delta,
      isDirty: delta !== null,
      isLoading,
      isSuccess,
    }),
    [local, setLocal, changeLocal, save, reset, delta, isLoading, isSuccess],
  )
}

const updateSiteInputAccessors: Accessors<
  GetSiteQuery[`site`],
  UpdateSiteInput
> = {
  id: get,
  address: get,
  latitude: get,
  longitude: get,
}

const createUpdateSiteInput = createInputBuilder(updateSiteInputAccessors)

export type UseSite = () => {
  local: GetSiteQuery[`site`]
  setLocal: SetterOrUpdater<GetSiteQuery[`site`]>
  changeLocal: (update: Partial<GetSiteQuery[`site`]>) => void
  save: (options?: SaveOptions) => Promise<void>
  reset: VoidFunction
  delta: Partial<GetSiteQuery[`site`]> | null
  isDirty: boolean
  isLoading: boolean
  isSuccess: boolean
}
